import React, { useMemo, useState } from 'react';
import {
    RefreshCw,
    Briefcase,
    Navigation,
    Clock,
    MapPin,
    ExternalLink,
    X,
    AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useLiveJobs from '../../hooks/useLiveJobs';
import LiveJobsList from './LiveJobsList';
import LiveJobsMap from './LiveJobsMap';
import VendorLiveTracking from '../VendorLiveTracking';
import LoadingSpinner from '../common/LoadingSpinner';
import ServiceStatusBadge from '../services/ServiceStatusBadge';
import { serviceStatusAllowsVendorTracking } from '../../utils/vendorTracking';
import { hasValidCoords } from '../../utils/locationCoords';

function formatAgo(date) {
    if (!date) return '—';
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
}

function KpiChip({ icon: Icon, label, value, tone = 'slate' }) {
    const tones = {
        slate: 'bg-slate-50 border-slate-200 text-slate-800',
        blue: 'bg-blue-50 border-blue-200 text-blue-900',
        teal: 'bg-teal-50 border-teal-200 text-teal-900',
        amber: 'bg-amber-50 border-amber-200 text-amber-900',
        red: 'bg-red-50 border-red-200 text-red-900',
    };
    return (
        <div className={`rounded-lg border px-3 py-2.5 ${tones[tone] || tones.slate}`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 opacity-70 shrink-0" />
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide opacity-70 font-medium">{label}</p>
                    <p className="text-lg font-bold leading-tight">{value}</p>
                </div>
            </div>
        </div>
    );
}

const LiveJobsSection = () => {
    const navigate = useNavigate();
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [trackingJob, setTrackingJob] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');

    const { jobs, driverLocations, statusCatalog, loading, error, lastUpdated, refresh } = useLiveJobs({
        enabled: true,
        autoRefresh,
    });

    const selectedJob = useMemo(
        () => jobs.find((j) => j.id === selectedJobId) || null,
        [jobs, selectedJobId],
    );

    const stats = useMemo(() => {
        const enRoute = jobs.filter((j) =>
            ['driver_started', 'vendor_reached_location', 'photos_submitted', 'otw_service_center'].includes(
                j.status,
            ),
        ).length;
        const pending = jobs.filter((j) =>
            ['pending', 'assigned', 'awaiting_customer_payment', 'vendor_scheduled', 'scheduled'].includes(
                j.status,
            ),
        ).length;
        const onMap = jobs.filter((j) => hasValidCoords(j.pickup_location)).length;
        const drivers = driverLocations.filter((d) => hasValidCoords(d?.location)).length;
        const slaBreached = jobs.filter((j) => j.sla_state === 'breached').length;
        const slaRisk = jobs.filter((j) => j.sla_state === 'at_risk').length;
        return { enRoute, pending, onMap, drivers, slaBreached, slaRisk };
    }, [jobs, driverLocations]);

    const handleSelectJob = (job) => {
        setSelectedJobId(job?.id ?? null);
    };

    const handleTrackJob = (job) => {
        if (!job) return;
        if (serviceStatusAllowsVendorTracking(job.status)) {
            setTrackingJob(job);
            setSelectedJobId(job.id);
            return;
        }
        navigate(`/add-service?case_id=${job.case_id}&service_id=${job.id}`);
    };

    const handleOpenService = (job) => {
        if (!job) return;
        navigate(
            `/add-service?case_id=${job.case_id}&service_id=${job.id}${
                serviceStatusAllowsVendorTracking(job.status) ? '&track=1' : ''
            }`,
        );
    };

    return (
        <div className="mrgall rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Live Jobs</h2>
                        <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                </span>
                                Live
                            </span>
                            <span className="text-gray-300">·</span>
                            Updated {formatAgo(lastUpdated)}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600"
                            />
                            Auto-refresh
                        </label>
                        <button
                            type="button"
                            onClick={refresh}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-900 disabled:opacity-50 transition-colors"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* KPI row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
                    <KpiChip icon={Briefcase} label="Active jobs" value={jobs.length} tone="blue" />
                    <KpiChip icon={Navigation} label="En route" value={stats.enRoute} tone="teal" />
                    <KpiChip icon={Clock} label="Pending / assigned" value={stats.pending} tone="amber" />
                    <KpiChip
                        icon={AlertTriangle}
                        label="SLA risk"
                        value={`${stats.slaBreached} breach · ${stats.slaRisk} risk`}
                        tone={stats.slaBreached ? 'red' : stats.slaRisk ? 'amber' : 'slate'}
                    />
                    <KpiChip
                        icon={MapPin}
                        label="On map"
                        value={`${stats.onMap} jobs · ${stats.drivers} drivers`}
                        tone="slate"
                    />
                </div>
            </div>

            {error ? (
                <div className="mx-4 md:mx-6 mt-4 rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            ) : null}

            <div className="p-4 md:p-6">
                {loading && jobs.length === 0 ? (
                    <LoadingSpinner />
                ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                        <div className="xl:col-span-7">
                            <LiveJobsList
                                jobs={jobs}
                                statusCatalog={statusCatalog}
                                selectedJobId={selectedJobId}
                                statusFilter={statusFilter}
                                onStatusFilterChange={setStatusFilter}
                                onSelectJob={handleSelectJob}
                                onTrackJob={handleTrackJob}
                            />
                        </div>
                        <div className="xl:col-span-5">
                            <LiveJobsMap
                                jobs={jobs}
                                driverLocations={driverLocations}
                                statusCatalog={statusCatalog}
                                selectedJobId={selectedJobId}
                                selectedJob={selectedJob}
                                onSelectJob={handleSelectJob}
                            />
                        </div>
                    </div>
                )}

                {/* Selected job action bar */}
                {selectedJob ? (
                    <div className="mt-4 rounded-xl border border-gray-200 bg-slate-50 p-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-1">
                                Selected job
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-mono font-bold text-gray-900">{selectedJob.service_number}</span>
                                <ServiceStatusBadge
                                    status={selectedJob.status}
                                    catalog={statusCatalog}
                                    compact
                                />
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                                {selectedJob.customer_name}
                                <span className="text-gray-300 mx-1">·</span>
                                {selectedJob.vendor_name}
                                {selectedJob.driver_name && selectedJob.driver_name !== '—' ? (
                                    <>
                                        <span className="text-gray-300 mx-1">·</span>
                                        {selectedJob.driver_name}
                                    </>
                                ) : null}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => setSelectedJobId(null)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm hover:bg-gray-50"
                            >
                                <X className="w-4 h-4" />
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={() => handleOpenService(selectedJob)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-800 text-sm font-medium hover:bg-gray-50"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Open service
                            </button>
                            {serviceStatusAllowsVendorTracking(selectedJob.status) ? (
                                <button
                                    type="button"
                                    onClick={() => setTrackingJob(selectedJob)}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-medium hover:bg-teal-800"
                                >
                                    <Navigation className="w-4 h-4" />
                                    Track live
                                </button>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {trackingJob ? (
                    <div className="mt-4 rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="flex items-center justify-between px-4 py-3 bg-slate-800 text-white">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-slate-300">Live tracking</p>
                                <p className="font-mono font-semibold">{trackingJob.service_number}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setTrackingJob(null)}
                                className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
                                aria-label="Close tracking"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <VendorLiveTracking serviceId={trackingJob.id} />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default LiveJobsSection;
